<div class="teams-page">
  <!-- Toolbar -->
  <mat-card class="teams-toolbar">
    <mat-card-content class="toolbar-content">

      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search teams</mat-label>
        <input matInput type="text" [(ngModel)]="searchTerm" (input)="onSearchChange()" placeholder="Search by name...">
        <mat-icon matPrefix>search</mat-icon>
        <button *ngIf="searchTerm" matSuffix mat-icon-button (click)="searchTerm=''; onSearchChange()">
          <mat-icon>close</mat-icon>
        </button>
      </mat-form-field>

      <span class="toolbar-spacer"></span>

      <button mat-raised-button color="primary" (click)="openAddModal()">
        <mat-icon>add</mat-icon> Add New Team
      </button>
    </mat-card-content>
  </mat-card>

  <!-- List View: mat-table -->
  <mat-card class="teams-table-card">
    <mat-progress-bar *ngIf="isLoading" mode="indeterminate"></mat-progress-bar>

    <table mat-table [dataSource]="dataSource" matSort class="teams-table">

      <!-- Name Column -->
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Team Name</th>
        <td mat-cell *matCellDef="let team">
          <div class="team-name-cell">
            <mat-icon color="primary">people</mat-icon>
            <span class="team-name-text">{{ team.name }}</span>
            <span *ngIf="team.isSystemTeam()" class="system-badge">
              <mat-icon class="badge-icon">lock</mat-icon> System
            </span>
          </div>
        </td>
      </ng-container>

      <!-- User Count Column -->
      <ng-container matColumnDef="userCount">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Members</th>
        <td mat-cell *matCellDef="let team">
          <span class="member-count">
            <mat-icon class="count-icon">group</mat-icon>
            {{ team.userCount || 0 }}
          </span>
        </td>
      </ng-container>

      <!-- Status Column -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
        <td mat-cell *matCellDef="let team">
          <span class="status-chip" [class.chip-protected]="!team.isDeletable" [class.chip-unprotected]="team.isDeletable">
            {{ team.isDeletable ? 'Not protected' : 'Protected' }}
          </span>
        </td>
      </ng-container>

      <!-- Actions Column -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="actions-header"></th>
        <td mat-cell *matCellDef="let team" class="actions-cell">
          <button mat-icon-button [matMenuTriggerFor]="teamMenu">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #teamMenu="matMenu">
            <button mat-menu-item (click)="openDetailModal(team)">
              <mat-icon>visibility</mat-icon>
              <span>View Details</span>
            </button>
            <button mat-menu-item (click)="openEditModal(team)"
              [disabled]="team.isSystemTeam()"
              [matTooltip]="team.isSystemTeam() ? 'System teams cannot be edited' : ''">
              <mat-icon>edit</mat-icon>
              <span>Edit Team</span>
            </button>
            <mat-divider></mat-divider>
            <button mat-menu-item (click)="openDeleteModal(team)" [disabled]="team.isSystemTeam()"
              [matTooltip]="team.isSystemTeam() ? 'System teams cannot be deleted' : ''">
              <mat-icon color="warn">delete</mat-icon>
              <span>Delete Team</span>
            </button>
          </mat-menu>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>

      <!-- Empty row -->
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell no-data-cell" [attr.colspan]="displayedColumns.length">
          <div class="no-data-message">
            <mat-icon>inbox</mat-icon>
            <p>No teams found</p>
          </div>
        </td>
      </tr>
    </table>

    <mat-paginator [pageSizeOptions]="pageSizeOptions" [pageSize]="pageSize" appPersistPageSize="teams:list" showFirstLastButtons></mat-paginator>
  </mat-card>

</div>

<!-- Add Team Modal -->
<div *ngIf="showAddModal" class="modal-backdrop">
  <mat-card class="modal-card">
    <mat-card-header class="modal-header">
      <mat-card-title class="modal-title">
        <span class="modal-title-content">
          <mat-icon>add_circle</mat-icon> Add New Team
        </span>
        <button mat-icon-button (click)="closeAddModal()" type="button" class="modal-close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </mat-card-title>
    </mat-card-header>
    <form [formGroup]="teamForm" (ngSubmit)="saveNewTeam()">
      <mat-card-content class="modal-body">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Team Name</mat-label>
          <input matInput formControlName="name" placeholder="Enter team name" required>
          <mat-icon matPrefix>people</mat-icon>
          <mat-error *ngIf="teamForm.get('name')?.hasError('required')">Team name is required</mat-error>
          <mat-error *ngIf="teamForm.get('name')?.hasError('minlength')">Minimum 2 characters required</mat-error>
        </mat-form-field>

        <mat-card class="info-card">
          <mat-card-content>
            <mat-checkbox formControlName="isDeletable">
              Allow this team to be deleted
            </mat-checkbox>
            <p class="checkbox-hint">
              <mat-icon class="hint-icon" color="primary">info</mat-icon>
              Uncheck this to protect the team from deletion (recommended for system teams)
            </p>
          </mat-card-content>
        </mat-card>
      </mat-card-content>
      <mat-divider></mat-divider>
      <mat-card-actions class="modal-actions">
        <button mat-button type="button" (click)="closeAddModal()">Cancel</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="teamForm.invalid || isLoading">
          <mat-icon>save</mat-icon> Create Team
        </button>
      </mat-card-actions>
    </form>
  </mat-card>
</div>

<!-- Edit Team Modal -->
<div *ngIf="showEditModal" class="modal-backdrop">
  <mat-card class="modal-card">
    <mat-card-header class="modal-header">
      <mat-card-title class="modal-title">
        <span class="modal-title-content">
          <mat-icon>edit</mat-icon> Edit Team
        </span>
        <button mat-icon-button (click)="closeEditModal()" type="button" class="modal-close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </mat-card-title>
    </mat-card-header>
    <form [formGroup]="teamForm" (ngSubmit)="updateTeam()">
      <mat-card-content class="modal-body">
        <mat-card *ngIf="selectedTeam?.isSystemTeam()" class="system-warning-card">
          <mat-card-content class="warning-content">
            <mat-icon>lock</mat-icon>
            <span>This is a protected system team. Name and protection status cannot be changed.</span>
          </mat-card-content>
        </mat-card>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Team Name</mat-label>
          <input matInput formControlName="name" placeholder="Enter team name" required>
          <mat-icon matPrefix>people</mat-icon>
          <mat-error *ngIf="teamForm.get('name')?.hasError('required')">Team name is required</mat-error>
          <mat-error *ngIf="teamForm.get('name')?.hasError('minlength')">Minimum 2 characters required</mat-error>
        </mat-form-field>

        <mat-card class="info-card">
          <mat-card-content>
            <mat-checkbox formControlName="isDeletable">
              Allow this team to be deleted
            </mat-checkbox>
          </mat-card-content>
        </mat-card>
      </mat-card-content>
      <mat-divider></mat-divider>
      <mat-card-actions class="modal-actions">
        <button mat-button type="button" (click)="closeEditModal()">Cancel</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="teamForm.invalid || isLoading">
          <mat-icon>save</mat-icon> Update Team
        </button>
      </mat-card-actions>
    </form>
  </mat-card>
</div>

<!-- View Details Modal -->
<div *ngIf="showDetailModal" class="modal-backdrop">
  <mat-card class="modal-card modal-wide">
    <mat-card-header class="modal-header">
      <mat-card-title class="modal-title">
        <span class="modal-title-content">
          <mat-icon>visibility</mat-icon> Team Details
        </span>
        <button mat-icon-button (click)="closeDetailModal()" type="button" class="modal-close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </mat-card-title>
    </mat-card-header>
    <mat-card-content class="modal-body detail-body">
      <div class="detail-header-row">
        <h3 class="detail-team-name">{{ selectedTeam?.name }}</h3>
        <div class="detail-meta">
          <span class="status-chip" [class.chip-protected]="!selectedTeam?.isDeletable" [class.chip-unprotected]="selectedTeam?.isDeletable">
            {{ selectedTeam?.isDeletable ? 'Not protected' : 'Protected' }}
          </span>
          <span class="member-count">
            <mat-icon class="count-icon">group</mat-icon>
            {{ teamUsers.length }} total members
          </span>
        </div>
      </div>

      <mat-divider></mat-divider>

      <h4 class="section-title">
        <mat-icon color="primary">people</mat-icon> Team Members ({{ teamUsers.length }})
      </h4>
      <mat-list *ngIf="teamUsers.length > 0">
        <mat-list-item *ngFor="let user of teamUsers">
          <div matListItemAvatar class="user-avatar">{{ user.name.substring(0, 1) || '?' }}</div>
          <div matListItemTitle>{{ user.name }}</div>
          <div matListItemLine>{{ user.loginId }}</div>
          <div matListItemMeta class="role-chips">
            <span *ngFor="let role of user.roles" class="role-chip">{{ role }}</span>
          </div>
          <mat-divider></mat-divider>
        </mat-list-item>
      </mat-list>
      <div *ngIf="teamUsers.length === 0" class="empty-members">
        <mat-icon>people_outline</mat-icon>
        <p>No members in this team</p>
      </div>
    </mat-card-content>
    <mat-divider></mat-divider>
    <mat-card-actions class="modal-actions">
      <button mat-raised-button color="primary" (click)="closeDetailModal()">Close</button>
    </mat-card-actions>
  </mat-card>
</div>

<!-- Delete Team Modal with Reassignment Workflow -->
<div *ngIf="showDeleteModal" class="modal-backdrop">
  <mat-card class="modal-card modal-wide">
    <mat-card-header class="modal-header modal-header-warn">
      <mat-card-title class="modal-title">
        <span class="modal-title-content">
          <mat-icon>warning</mat-icon> Delete Team
        </span>
        <button mat-icon-button (click)="closeDeleteModal()" type="button" class="modal-close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </mat-card-title>
    </mat-card-header>

    <mat-card-content class="modal-body delete-body">
      <mat-stepper #deleteStepper [linear]="true" class="delete-stepper">

        <!-- Step 1: Select Users -->
        <mat-step label="Reassign Users" [completed]="deleteStep !== 'select-users'">
          <mat-card class="step-alert step-alert-warn">
            <mat-card-content class="alert-content">
              <mat-icon>warning</mat-icon>
              <span>Team "{{ selectedTeam?.name }}" has {{ teamUsers.length }} members. Select users to reassign before deleting.</span>
            </mat-card-content>
          </mat-card>

          <div class="selection-controls">
            <button mat-button type="button" (click)="selectAllUsers()">
              <mat-icon>check_box</mat-icon> Select All
            </button>
            <button mat-button type="button" (click)="clearAllUsers()">
              <mat-icon>check_box_outline_blank</mat-icon> Clear All
            </button>
            <span class="selection-count">{{ reassignSelectedUserIds.length }} of {{ teamUsers.length }} selected</span>
          </div>

          <mat-list>
            <mat-list-item *ngFor="let user of teamUsers" (click)="toggleUserSelection(user.id)"
              class="user-list-item" [class.user-selected]="isUserSelected(user.id)">
              <mat-checkbox matListItemMeta [checked]="isUserSelected(user.id)"
                (click)="$event.stopPropagation()" (change)="toggleUserSelection(user.id)"></mat-checkbox>
              <div matListItemAvatar class="user-avatar">{{ user.name.substring(0, 1) || '?' }}</div>
              <div matListItemTitle>{{ user.name }}</div>
              <div matListItemLine>{{ user.loginId }}</div>
              <mat-divider></mat-divider>
            </mat-list-item>
          </mat-list>
        </mat-step>

        <!-- Step 2: Select Target Team -->
        <mat-step label="Select Target" [completed]="deleteStep === 'confirm'">
          <mat-card class="step-alert step-alert-info">
            <mat-card-content class="alert-content">
              <mat-icon>info</mat-icon>
              <span>Reassigning {{ reassignSelectedUserIds.length }} users. Select the target team.</span>
            </mat-card-content>
          </mat-card>

          <mat-radio-group [(ngModel)]="reassignTargetTeamId" class="target-team-radio-group" required>
            <mat-radio-button
              *ngFor="let team of getAvailableTargetTeams()"
              [value]="team.id"
              class="target-team-radio">
              <span class="radio-team-name">{{ team.name }}</span>
              <span class="radio-team-count">{{ team.userCount || 0 }} members</span>
            </mat-radio-button>
          </mat-radio-group>

          <div *ngIf="getAvailableTargetTeams().length === 0" class="no-target-teams">
            <mat-icon>warning</mat-icon>
            <p>No other teams available to reassign users to.</p>
          </div>
        </mat-step>

        <!-- Step 3: Confirmation -->
        <mat-step label="Confirm Delete" [completed]="false">
          <mat-card *ngIf="teamUsers.length > 0" class="step-alert step-alert-success">
            <mat-card-content>
              <div class="alert-content">
                <mat-icon>check_circle</mat-icon>
                <div>
                  <p class="alert-title">Successfully reassigned {{ reassignSelectedUserIds.length }} users to "{{ getTargetTeamName() }}".</p>
                  <p>Team still has {{ teamUsers.length }} members. Continue reassigning or proceed with deletion.</p>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card *ngIf="teamUsers.length === 0" class="step-alert step-alert-success">
            <mat-card-content class="alert-content">
              <mat-icon>check_circle</mat-icon>
              <span>All users successfully reassigned. "{{ selectedTeam?.name }}" is now empty and can be deleted.</span>
            </mat-card-content>
          </mat-card>

          <mat-card *ngIf="teamUsers.length === 0" class="step-alert step-alert-danger">
            <mat-card-content class="danger-confirm">
              <mat-icon class="danger-icon">warning</mat-icon>
              <h3>Delete team "{{ selectedTeam?.name }}"?</h3>
              <p>This action cannot be undone.</p>
            </mat-card-content>
          </mat-card>
        </mat-step>

      </mat-stepper>
    </mat-card-content>

    <mat-divider></mat-divider>
    <mat-card-actions class="modal-actions delete-modal-actions">
      <button mat-button (click)="closeDeleteModal()">Cancel</button>
      <div class="step-btns">
        <!-- Step 1 -->
        <button *ngIf="deleteStep === 'select-users'" mat-raised-button color="primary"
          (click)="proceedToTargetSelection()" [disabled]="!canProceedToTargetSelection">
          Next: Select Target Team <mat-icon>arrow_forward</mat-icon>
        </button>
        <!-- Step 2 -->
        <button *ngIf="deleteStep === 'select-target'" mat-button (click)="backToUserSelection()">
          <mat-icon>arrow_back</mat-icon> Back
        </button>
        <button *ngIf="deleteStep === 'select-target'" mat-raised-button color="primary"
          (click)="proceedToConfirmation()" [disabled]="!canConfirmReassignment">
          Review &amp; Confirm <mat-icon>arrow_forward</mat-icon>
        </button>
        <!-- Step 3 with remaining users -->
        <button *ngIf="deleteStep === 'confirm' && teamUsers.length > 0" mat-button (click)="backToUserSelection()">
          <mat-icon>arrow_back</mat-icon> Reassign More Users
        </button>
        <button *ngIf="deleteStep === 'confirm' && teamUsers.length > 0" mat-raised-button color="primary"
          (click)="executeReassignment()" [disabled]="isReassigning">
          <mat-icon>sync</mat-icon> Execute Reassignment
        </button>
        <!-- Step 3 no users -->
        <button *ngIf="deleteStep === 'confirm' && teamUsers.length === 0" mat-raised-button color="warn"
          (click)="confirmDelete()" [disabled]="isLoading">
          <mat-icon>delete</mat-icon> Delete Team
        </button>
      </div>
    </mat-card-actions>
  </mat-card>
</div>
